import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Paper,
  Modal,
  IconButton,
  DialogContent,
  MenuItem,
  TablePagination,
} from '@mui/material';
import {
  BootstrapDialog,
  BootstrapDialogTitle,
  Breadcrumb,
  CTSinput,
  CTSRinput,
  StyledTableCell,
  StyledTableRow,
} from '../../reusableComponent/Reusables';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { Print } from '@mui/icons-material';
import styles from '../../../styles.module.css';
import dayjs from 'dayjs';
import ReusableSnackbar from '../../reusableComponent/ReusableSnackbar';
import maintenance from '../../../services/maintenance';
import customerMasterService from '../../../services/masters/customerMaster.Service';
import cylinderMasterService from '../../../services/masters/cylinderMaster.Service';

const printStyles = `
  .print-header-info {
    display: none;
  }

  @media print {
    .print-header-info {
      display: block !important;
      margin-bottom: 10px;
    }

    body * {
      visibility: hidden;
    }

    .printable-area, .printable-area * {
      visibility: visible;
    }

    .printable-area {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
    }

    .printable-table {
      width: 100% !important;
      border-collapse: collapse !important;
      overflow: visible !important;
    }

    .printable-table thead {
      display: table-header-group;
    }

    .printable-table tbody {
      display: table-row-group;
    }

    .MuiPaper-root,
    .MuiTableContainer-root {
      overflow: visible !important;
      max-height: none !important;
      box-shadow: none !important;
    }

    .no-print {
      display: none !important;
    }
  }
`;

const MaintenanceReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state || null;

  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD?.result?.userInfo;
  const depot_id = userInfo?.depot_id;

  const [successMessage, setSuccessMessage] = useState('');
  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [customer, setCustomer] = useState([]);
  const [cylinder, setCylinder] = useState([]);
  const [info, setInfo] = useState([]);
  const [openDetail, setOpenDetail] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState('');

  const {
    control,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      entry_date_from: editData?.entry_date ? dayjs(editData.entry_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      entry_date_to: '',
      owner: '',
      customer_id: '',
    },
  });

  const entryDateFrom = watch('entry_date_from');
  const entryDateTo = watch('entry_date_to');
  const selectedOwner = watch('owner');
  const selectedCustomerId = watch('customer_id');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [customerResponse, cylinderResponse] = await Promise.all([
          customerMasterService.getCustomerList({ page: 0, size: 999, searchKey: '' }),
          cylinderMasterService.getCylinderList({ page: 0, size: 999, searchKey: '' }),
        ]);

        setCustomer(customerResponse?.data || []);
        setCylinder(cylinderResponse?.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSuccessMessage('Error fetching customer or cylinder data');
        setCustomer([]);
        setCylinder([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);

  const handleShow = useCallback(async (formData) => {
    const { entry_date_from, entry_date_to, owner, customer_id } = formData;

    // Validation
    if (!entry_date_from) {
      setError('entry_date_from', { message: 'From Date is required' });
      return;
    }

    if (!entry_date_to) {
      setError('entry_date_to', { message: 'To Date is required' });
      return;
    }

    if (!owner || owner === '') {
      setError('owner', { message: 'Owner is required' });
      return;
    }

    if (owner === 'customer' && (!customer_id || customer_id === '')) {
      setError('customer_id', { message: 'Customer is required' });
      return;
    }

    if (dayjs(entry_date_to).isBefore(dayjs(entry_date_from))) {
      setError('entry_date_to', { message: 'To Date cannot be before From Date' });
      return;
    }

    try {
      setLoading(true);
      setDisplayData([]);

      let filterOwner = '';
      if (owner === 'all') {
        filterOwner = '';
      } else if (owner === 'customer') {
        filterOwner = 'customer';
      } else {
        filterOwner = owner;
      }

      const filterParams = {
        page,
        size: rowsPerPage,
        searchKey: '',
        fromDate: entry_date_from,
        toDate: entry_date_to,
        owner: filterOwner,
        customer_id: owner === 'customer' ? customer_id : '',
      };

      console.log('Filter Params:', filterParams);
      const response = await maintenance.getmaintenanceReportList(filterParams);
      console.log('API Response:', response);

      const fetchedData = response?.data || response || [];
      const total = response?.total_count || fetchedData.length || 0;

      const mappedData = fetchedData.map((item) => ({
        owner: item.owner || 'N/A',
        entry_date: item.entry_date,
        cyl_no: item.cylinder_no || item.cyl_id || 'N/A',
        tag_no: item.tag_no || 'N/A',
        valve: item.leakage_from_valve || 'N/A',
        cylinder_neck_thread: item.leakage_from_cyl_neck_thread || 'N/A',
        fusible_plug: item.leakage_from_fusible_plug || 'N/A',
        cylinder_cap: item.cylinder_cap || 'N/A',
        product_code: item.short_code || 'N/A',
        painting: item.painting || 'N/A',
        cylinder_tag: item.tag_not_readable || 'N/A',
        testing_result: item.result || 'N/A',
        remark: item.remarks || 'N/A',
      }));

      console.log('Mapped Data:', mappedData);
      setDisplayData(mappedData);
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching maintenance report:', error);
      setSuccessMessage('Error fetching maintenance report');
      setDisplayData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [setError, page, rowsPerPage]);

  useEffect(() => {
    setPage(0); // Reset page on filter change
    if (
      entryDateFrom &&
      entryDateTo &&
      selectedOwner &&
      (selectedOwner !== 'customer' || (selectedOwner === 'customer' && selectedCustomerId)) &&
      !errors.entry_date_from &&
      !errors.entry_date_to &&
      !errors.owner &&
      !errors.customer_id
    ) {
      handleShow({
        entry_date_from: entryDateFrom,
        entry_date_to: entryDateTo,
        owner: selectedOwner,
        customer_id: selectedCustomerId,
      });
    }
  }, [entryDateFrom, entryDateTo, selectedOwner, selectedCustomerId, errors, handleShow]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage('');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDetails = (tableData) => {
    try {
      const parsedData = JSON.parse(tableData);
      setInfo(parsedData);
      setOpenDetail(true);
    } catch (error) {
      console.error('Error parsing table data:', error);
      setSuccessMessage('Error displaying details');
    }
  };

  const dateConversion = (date) => {
    if (!date) return '';
    const today = new Date(date);
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleClose = () => {
    setOpenDetail(false);
  };

  const getUniqueOwners = () => {
    const owners = [...new Set(cylinder.map((item) => item.owner).filter(Boolean))];
    return owners;
  };

  return (
    <Box className={styles.cts_page} sx={{ padding: '0px !important', margin: '0px !important' }}>
      <Box className={styles.body}>
        <Breadcrumb routeSegments={[{ name: 'Maintenance Report' }]} />
        <Typography className={styles.header_txt}>MAINTENANCE REPORT</Typography>

        <form className="no-print">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={styles.label_Txt_add}>From Date</Typography>
                <Controller
                  name="entry_date_from"
                  control={control}
                  render={({ field }) => (
                    <CTSRinput
                      size="small"
                      fullWidth
                      type="date"
                      {...field}
                      error={!!errors.entry_date_from}
                      helperText={errors.entry_date_from?.message}
                      InputProps={{ className: styles.CTSinput_Box }}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={styles.label_Txt_add}>To Date</Typography>
                <Controller
                  name="entry_date_to"
                  control={control}
                  render={({ field }) => (
                    <CTSRinput
                      size="small"
                      fullWidth
                      type="date"
                      {...field}
                      error={!!errors.entry_date_to}
                      helperText={errors.entry_date_to?.message}
                      InputProps={{ className: styles.CTSinput_Box }}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={styles.label_Txt_add}>Owner</Typography>
                <Controller
                  name="owner"
                  control={control}
                  rules={{ required: 'Owner is required' }}
                  render={({ field }) => (
                    <CTSRinput
                      select
                      size="small"
                      fullWidth
                      {...field}
                      error={!!errors.owner}
                      helperText={errors.owner?.message}
                      InputProps={{ className: styles.CTSinput_Box }}
                      onChange={(e) => {
                        field.onChange(e);
                        setSelectedCustomer('');
                        clearErrors('customer_id');
                      }}
                    >
                      <MenuItem value="">Select Owner</MenuItem>
                      <MenuItem value="all">All Owners</MenuItem>
                      <MenuItem value="tgpl">TGPL</MenuItem>
                      <MenuItem value="customer">Customer</MenuItem>
                      {getUniqueOwners().map((owner) => (
                        <MenuItem key={owner} value={owner}>
                          {owner}
                        </MenuItem>
                      ))}
                    </CTSRinput>
                  )}
                />
              </Box>
            </Grid>

            {selectedOwner === 'customer' && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography className={styles.label_Txt_add}>Customer</Typography>
                  <Controller
                    name="customer_id"
                    control={control}
                    rules={{ required: 'Customer is required' }}
                    render={({ field }) => (
                      <CTSRinput
                        select
                        size="small"
                        fullWidth
                        {...field}
                        error={!!errors.customer_id}
                        helperText={errors.customer_id?.message}
                        InputProps={{ className: styles.CTSinput_Box }}
                        onChange={(e) => {
                          field.onChange(e);
                          setSelectedCustomer(e.target.value);
                        }}
                      >
                        <MenuItem value="">Select Customer</MenuItem>
                        {customer.map((cust) => (
                          <MenuItem key={cust.customer_id} value={cust.customer_id}>
                            {cust.customer_name}
                          </MenuItem>
                        ))}
                      </CTSRinput>
                    )}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </form>

        <Grid item xs={12} sx={{ mt: 4 }}>
          <Paper sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '10px' }}>
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={displayData.length === 0}
                className="no-print"
              >
                Print
              </Button>
            </Box>

            <div className="printable-area">
              <div className="print-header-info" style={{ marginBottom: '20px' }}>
                <Typography variant="h4" align="left" sx={{ fontWeight: 'bold' }}>
                  Maintenance Report
                </Typography>
                <Typography variant="body1" align="left" sx={{ marginTop: 1 }}>
                  From: {entryDateFrom} &nbsp;&nbsp; | &nbsp;&nbsp; To: {entryDateTo} &nbsp;&nbsp; | &nbsp;&nbsp; Owner:{' '}
                  {selectedOwner === 'all'
                    ? 'All Owners'
                    : selectedOwner === 'tgpl'
                    ? 'TGPL'
                    : selectedOwner === 'customer'
                    ? customer.find((cust) => cust.customer_id === selectedCustomerId)?.customer_name || 'Customer'
                    : selectedOwner}
                </Typography>
              </div>
              <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
                <Table size="small" stickyHeader className="printable-table">
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Owner</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Date</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Cylinder No</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Tag No</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Valve</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Cylinder Neck Thread</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Fusible Plug</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Cylinder Cap</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Painting</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Cylinder Tag</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Testing Result</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left !important', paddingLeft: '5px' }}>Remark</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>

                  <TableBody sx={{ backgroundColor: '#fff' }}>
                    {loading ? (
                      <StyledTableRow>
                        <StyledTableCell colSpan={13} align="center">
                          <Typography>Loading data...</Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : displayData.length === 0 ? (
                      <StyledTableRow>
                        <StyledTableCell colSpan={13} align="center">
                          <Typography>No data found</Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : (
                      displayData.map((row, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {row.owner}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {dateConversion(row.entry_date)}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>{row.cyl_no}</StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>{row.tag_no}</StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>{row.valve}</StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {row.cylinder_neck_thread}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {row.fusible_plug}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {row.cylinder_cap}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>{row.painting}</StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {row.cylinder_tag}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>
                            {row.testing_result}
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.inspection_cell}>{row.remark}</StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <TablePagination
              className="no-print"
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>

        <ReusableSnackbar
          open={!!successMessage}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
          className="no-print"
        />

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="image-modal-title"
          aria-describedby="image-modal-description"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          className="no-print"
        >
          <Box
            sx={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 2,
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              onClick={handleCloseModal}
              aria-label="Close image modal"
            >
              <CloseIcon />
            </IconButton>
            <img
              src={selectedImage}
              alt="Full-size receipt"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              onError={(e) => {
                e.target.onerror = null;
              }}
            />
          </Box>
        </Modal>

        <BootstrapDialog
          PaperProps={{
            sx: {
              minWidth: '90%',
              maxHeight: '90%',
            },
          }}
          onClose={handleClose}
          open={openDetail}
        >
          <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
            Details
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
              <Table size="small" stickyHeader className="printable-table">
                <TableHead className={styles.table_head_container}>
                  <StyledTableRow>c
                    <StyledTableCell align="center">Sr No</StyledTableCell>
                    <StyledTableCell align="left">Cylinder No</StyledTableCell>
                    <StyledTableCell align="left">Tag No</StyledTableCell>
                    <StyledTableCell align="left">Initial Wt</StyledTableCell>
                    <StyledTableCell align="left">Final Wt</StyledTableCell>
                    <StyledTableCell align="left">Qty Filled</StyledTableCell>
                    <StyledTableCell align="left">Remarks</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#fff' }}>
                  {info.map((row, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell align="center">{row.sr_no}</StyledTableCell>
                      <StyledTableCell className={styles.inspection_cell}>{row.cyl_no}</StyledTableCell>
                      <StyledTableCell className={styles.inspection_cell}>{row.tag_no}</StyledTableCell>
                      <StyledTableCell className={styles.inspection_cell}>{row.initial_wt}</StyledTableCell>
                      <StyledTableCell className={styles.inspection_cell}>{row.final_wt}</StyledTableCell>
                      <StyledTableCell className={styles.inspection_cell}>
                        {row.qty_filled} {row.uom}
                      </StyledTableCell>
                      <StyledTableCell className={styles.inspection_cell}>{row.remarks}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </BootstrapDialog>
      </Box>
    </Box>
  );
};

export default MaintenanceReport;